import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useAuth } from "@/hooks/useAuth";
import { Info, Loader2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { resolveFunnelSelectLabel } from "@/lib/funnel-display-label";
import { readInvokeErrorText } from "@/lib/invokeError";
import { logInternalError } from "@/lib/safe-error-message";
import { useWorkspaceIntegrationsMap } from "@/hooks/useWorkspaceIntegrationsMap";

function FieldLabelHelp({ content, ariaLabel }: { content: string; ariaLabel: string }) {
  const [popoverOpen, setPopoverOpen] = useState(false);

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <Tooltip open={popoverOpen ? false : undefined} delayDuration={300}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={ariaLabel}
              className={cn(
                "inline-flex items-center justify-center rounded-sm p-0.5",
                "text-muted-foreground opacity-70 ring-offset-background transition-all",
                "hover:opacity-100 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              )}
            >
              <Info className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="max-w-[280px] text-xs leading-relaxed">
          Clique para mais informações
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        side="right"
        align="start"
        collisionPadding={16}
        className="max-w-[280px] text-xs leading-relaxed"
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}

export type WhatsAppQrProvider = "whatsapp_evolution" | "whatsapp_uazapi";
export type WhatsAppQrEdgeFunction = "evolution-connect" | "uazapi-connect";

interface WhatsAppQrConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: WhatsAppQrProvider;
  edgeFunction: WhatsAppQrEdgeFunction;
  title: string;
  description: string;
  badgeLabel?: "Legado" | "Novo";
  existingIntegration?: any;
  reconnectMode?: boolean;
  migrateFromIntegrationId?: string | null;
}

interface FunnelOption {
  id: string;
  name: string;
  is_default: boolean | null;
  integration_id: string | null;
}

const GENERIC_CONNECT_ERROR = "Não foi possível concluir a conexão. Tente novamente.";
const SESSION_EXPIRED_ERROR = "Sua sessão expirou. Atualize a página e tente novamente.";

async function invokeConnect(
  edgeFunction: WhatsAppQrEdgeFunction,
  body: Record<string, unknown>,
) {
  // Garante um token válido antes de chamar (sessão expirada era a causa de falhas silenciosas).
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData?.session) {
    throw new Error(SESSION_EXPIRED_ERROR);
  }

  const { data, error } = await supabase.functions.invoke(edgeFunction, { body });

  if (error) {
    const detail = await readInvokeErrorText(error, data);
    logInternalError("whatsapp-connect", error, { edgeFunction, action: body.action, detail });
    const status = (error as { context?: { status?: number } })?.context?.status;
    throw new Error(status === 401 ? SESSION_EXPIRED_ERROR : GENERIC_CONNECT_ERROR);
  }

  if (data?.success === false) {
    logInternalError("whatsapp-connect", data?.error, { edgeFunction, action: body.action });
    throw new Error(GENERIC_CONNECT_ERROR);
  }

  return data;
}

export function WhatsAppQrConnectDialog({
  open,
  onOpenChange,
  provider,
  edgeFunction,
  title,
  description,
  badgeLabel,
  existingIntegration,
  reconnectMode,
  migrateFromIntegrationId,
}: WhatsAppQrConnectDialogProps) {
  const isMigrateReconnect = Boolean(migrateFromIntegrationId);
  const [connecting, setConnecting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [connectionStatus, setConnectionStatus] = useState("");
  const [instanceIdentifier, setInstanceIdentifier] = useState("");
  const [integrationId, setIntegrationId] = useState<string | null>(null);
  const [selectedFunnelId, setSelectedFunnelId] = useState<string>("");
  const [funnelMode, setFunnelMode] = useState<"auto" | "existing">("auto");
  const [funnels, setFunnels] = useState<FunnelOption[]>([]);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const integrationById = useWorkspaceIntegrationsMap(workspaceId);

  const handleConnected = useCallback(() => {
    setQrCode("");
    setPairingCode("");
    setConnectionStatus("open");
    toast({ title: "Conectado!", description: "Seu WhatsApp foi conectado com sucesso" });
    onOpenChange(false);
  }, [toast, onOpenChange]);

  useEffect(() => {
    if (!open) {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setQrCode("");
      setPairingCode("");
      setConnectionStatus("");
      setIntegrationId(null);
      setConnecting(false);
      setFunnelMode("auto");
      setSelectedFunnelId("");
      setFunnels([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !workspaceId) return;

    const loadFunnels = async () => {
      const { data, error } = await supabase
        .from("sales_funnels")
        .select("id, name, is_default, integration_id")
        .eq("workspace_id", workspaceId)
        .order("is_default", { ascending: false })
        .order("order_index", { ascending: true });

      if (error) {
        console.error("Erro ao carregar funis:", error);
        return;
      }
      setFunnels((data || []) as FunnelOption[]);
    };

    loadFunnels();
  }, [open, workspaceId]);

  useEffect(() => {
    if (!open || !reconnectMode) return;
    const currentSelected = existingIntegration?.config?.selected_funnel_id as string | undefined;
    if (currentSelected) {
      setFunnelMode("existing");
      setSelectedFunnelId(currentSelected);
    } else {
      setFunnelMode("auto");
      setSelectedFunnelId("");
    }

    const existingQr = existingIntegration?.config?.qrcode?.base64 as string | undefined;
    const existingPairing = existingIntegration?.config?.qrcode?.pairingCode as string | undefined;
    if (existingQr && existingIntegration?.id) {
      setQrCode(existingQr);
      if (existingPairing) setPairingCode(existingPairing);
      setIntegrationId(existingIntegration.id);
      setInstanceIdentifier(existingIntegration.instance_identifier || "");
      setConnecting(false);
    }
  }, [open, reconnectMode, existingIntegration]);

  useEffect(() => {
    if (!integrationId || !open) return;

    const channel = supabase
      .channel(`qr-${provider}-${integrationId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "integrations",
          filter: `id=eq.${integrationId}`,
        },
        (payload) => {
          const newRow = payload.new as any;
          const config = newRow.config;

          if (newRow.status === "active") {
            handleConnected();
            return;
          }

          if (config?.qrcode?.base64) {
            setQrCode(config.qrcode.base64);
            if (config.qrcode.pairingCode) setPairingCode(config.qrcode.pairingCode);
            setConnecting(false);
          }
        },
      )
      .subscribe();

    channelRef.current = channel;

    const initialRead = async () => {
      const { data: integration } = await supabase
        .from("integrations")
        .select("config, status")
        .eq("id", integrationId)
        .single();

      if (!integration) return;

      const config = integration.config as any;
      if ((integration.status as string) === "active") {
        handleConnected();
        return;
      }
      if (config?.qrcode?.base64) {
        setQrCode(config.qrcode.base64);
        if (config.qrcode.pairingCode) setPairingCode(config.qrcode.pairingCode);
        setConnecting(false);
      }
    };
    initialRead();

    timeoutRef.current = setTimeout(() => {
      if (!qrCode) {
        toast({
          title: "Timeout",
          description: "QR code não foi recebido. Tente novamente.",
          variant: "destructive",
        });
        setConnecting(false);
      }
    }, 120000);

    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [integrationId, open, handleConnected, toast, provider, qrCode]);

  const startReconnect = async () => {
    if (!workspaceId || !existingIntegration) return;

    const identifier = existingIntegration.instance_identifier;
    if (!identifier) {
      toast({ title: "Erro", description: "Identificador da conexão não encontrado", variant: "destructive" });
      return;
    }

    if (funnelMode === "existing" && !selectedFunnelId) {
      toast({ title: "Erro", description: "Selecione um funil para continuar", variant: "destructive" });
      return;
    }

    setConnecting(true);
    try {
      const body: Record<string, unknown> = {
        workspaceId,
        selectedFunnelId: funnelMode === "existing" ? selectedFunnelId : null,
      };

      if (isMigrateReconnect && migrateFromIntegrationId) {
        body.action = "migrate_reconnect";
        body.sourceIntegrationId = migrateFromIntegrationId;
        body.instanceIdentifier = identifier;
      } else {
        body.action = "reconnect";
        body.instanceIdentifier = identifier;
      }

      const data = await invokeConnect(edgeFunction, body);

      setInstanceIdentifier(identifier);
      if (data.qrCode) {
        setQrCode(data.qrCode);
        if (data.pairingCode) setPairingCode(data.pairingCode);
        setConnecting(false);
      }
      setIntegrationId(data.integrationId);
      if (!data.qrCode) {
        toast({
          title: isMigrateReconnect ? "Reconectando" : "Reconectando",
          description: isMigrateReconnect
            ? "Reconectando seu WhatsApp..."
            : "Aguardando QR code...",
        });
      }
    } catch (error: any) {
      toast({
        title: "Erro ao reconectar",
        description: error.message || "Tente novamente",
        variant: "destructive",
      });
      setConnecting(false);
    }
  };

  const connectInstance = async () => {
    if (!workspaceId) {
      toast({ title: "Erro", description: "Workspace não encontrado", variant: "destructive" });
      return;
    }

    if (!instanceIdentifier.trim()) {
      toast({ title: "Erro", description: "Digite um identificador para a instância", variant: "destructive" });
      return;
    }
    if (funnelMode === "existing" && !selectedFunnelId) {
      toast({ title: "Erro", description: "Selecione um funil para continuar", variant: "destructive" });
      return;
    }

    setConnecting(true);

    try {
      const cleanIdentifier = instanceIdentifier.toLowerCase().replace(/[^a-z0-9_-]/g, "_");

      const data = await invokeConnect(edgeFunction, {
        workspaceId,
        action: "create",
        instanceIdentifier: cleanIdentifier,
        selectedFunnelId: funnelMode === "existing" ? selectedFunnelId : null,
      });

      setInstanceIdentifier(cleanIdentifier);

      if (data.qrCode) {
        setQrCode(data.qrCode);
        if (data.pairingCode) setPairingCode(data.pairingCode);
        setConnecting(false);
      }

      setIntegrationId(data.integrationId);

      if (!data.qrCode) {
        toast({ title: "Aguardando QR Code", description: "O QR code será carregado automaticamente..." });
      }
    } catch (error: any) {
      toast({
        title: "Erro ao conectar",
        description: error.message || "Tente novamente",
        variant: "destructive",
      });
      setConnecting(false);
    }
  };

  const handleCancel = async () => {
    if (!integrationId || !workspaceId) return;
    setCancelling(true);
    try {
      await invokeConnect(edgeFunction, {
        workspaceId,
        action: "delete",
        integrationId,
      });
      toast({ title: "Conexão removida", description: "A conexão foi cancelada e removida com sucesso" });
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Erro ao cancelar",
        description: error.message || "Tente novamente",
        variant: "destructive",
      });
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex w-[95vw] max-h-[95vh] flex-col overflow-hidden p-4 sm:max-w-md sm:p-6">
        <DialogHeader className="shrink-0 space-y-1.5 pr-10 text-left">
          <DialogTitle className="flex flex-wrap items-center gap-2 text-lg leading-snug sm:text-xl">
            {title}
            {badgeLabel ? (
              <Badge variant="secondary" className="text-xs font-normal">
                {badgeLabel}
              </Badge>
            ) : null}
          </DialogTitle>
          <DialogDescription className="text-pretty text-xs sm:text-sm">{description}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto sm:space-y-4">
          {!qrCode && !connecting && !integrationId && (
            <div className="space-y-4 py-6">
              {!reconnectMode && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="instanceIdentifier">Identificador da Conexão</Label>
                    <FieldLabelHelp
                      ariaLabel="Ajuda sobre o identificador da conexão"
                      content="Nome interno para identificar esta conexão WhatsApp no FabChat. Não é o número de telefone — serve para distinguir conexões quando você tiver mais de uma (ex.: vendas, suporte). Use apenas letras minúsculas, números, _ ou -."
                    />
                  </div>
                  <Input
                    id="instanceIdentifier"
                    placeholder="Ex: vendas, suporte, principal"
                    value={instanceIdentifier}
                    onChange={(e) => setInstanceIdentifier(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Use apenas letras minúsculas, números, _ ou -
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Label>Destino dos novos leads</Label>
                  <FieldLabelHelp
                    ariaLabel="Ajuda sobre o destino dos novos leads"
                    content="Define para onde vão os contatos que enviam mensagem pela primeira vez nesta conexão. Em Automático, o FabChat usa o roteamento padrão do workspace. Em Funil existente, você escolhe um funil específico para receber esses leads."
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={funnelMode === "auto" ? "default" : "outline"}
                    onClick={() => {
                      setFunnelMode("auto");
                      setSelectedFunnelId("");
                    }}
                    className="flex-1"
                  >
                    Automático
                  </Button>
                  <Button
                    type="button"
                    variant={funnelMode === "existing" ? "default" : "outline"}
                    onClick={() => setFunnelMode("existing")}
                    className="flex-1"
                  >
                    Funil existente
                  </Button>
                </div>
              </div>

              {funnelMode === "existing" && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="selectedFunnelId">Selecione o funil</Label>
                    <FieldLabelHelp
                      ariaLabel="Ajuda sobre a seleção do funil"
                      content="Funil de vendas em que os novos leads serão criados ao entrarem em contato por este WhatsApp. O lead aparecerá na primeira etapa desse funil."
                    />
                  </div>
                  <select
                    id="selectedFunnelId"
                    className="w-full border rounded-md px-3 py-2 bg-background text-sm"
                    value={selectedFunnelId}
                    onChange={(e) => setSelectedFunnelId(e.target.value)}
                  >
                    <option value="">Selecione...</option>
                    {funnels.map((funnel) => (
                      <option key={funnel.id} value={funnel.id}>
                        {resolveFunnelSelectLabel(funnel, integrationById)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <Button
                onClick={reconnectMode ? startReconnect : connectInstance}
                disabled={connecting || (!reconnectMode && !instanceIdentifier.trim())}
                size="lg"
                className="w-full"
              >
                {reconnectMode
                  ? isMigrateReconnect
                    ? "Reconectar WhatsApp"
                    : "Reconectar e gerar QR Code"
                  : "Gerar QR Code"}
              </Button>
            </div>
          )}

          {connecting && !qrCode && (
            <div className="flex flex-col items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-sm text-muted-foreground">
                {isMigrateReconnect ? "Reconectando seu WhatsApp..." : "Gerando QR code..."}
              </p>
            </div>
          )}

          {qrCode && (
            <div className="space-y-4">
              <div className="bg-muted p-6 rounded-lg">
                <p className="font-semibold mb-4 text-center">Escaneie o QR Code</p>
                <div className="flex justify-center mb-4 bg-white p-4 rounded-lg">
                  <img
                    src={qrCode}
                    alt="QR Code WhatsApp"
                    className="max-w-full h-auto"
                    style={{ maxWidth: "256px", maxHeight: "256px" }}
                    onError={(e) => {
                      console.error("Erro ao carregar imagem do QR code:", e);
                    }}
                  />
                </div>
                {pairingCode && (
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-1">Ou use o código de pareamento:</p>
                    <code className="text-lg font-bold">{pairingCode}</code>
                  </div>
                )}
                {connectionStatus && (
                  <p className="text-center text-sm text-muted-foreground mt-4">
                    Status: {connectionStatus === "open" ? "✅ Conectado" : "⏳ Aguardando conexão..."}
                  </p>
                )}
              </div>

              <Button
                onClick={reconnectMode ? startReconnect : connectInstance}
                variant="outline"
                className="w-full"
                disabled={connecting}
              >
                {connecting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Atualizar QR Code
              </Button>
            </div>
          )}

          {!reconnectMode && integrationId && (
            <Button
              onClick={handleCancel}
              variant="destructive"
              className="w-full"
              disabled={cancelling}
            >
              {cancelling && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Cancelar e remover conexão
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
